import { Component, Prop, Event, EventEmitter, Method, State, Element, h, getAssetPath } from '@stencil/core';
import { Color, Size } from '../../interface';
import { Button, DataIn, SipOpts } from './intercom-interface';
import { CameraOpts } from '../rtsp-h264-mjpeg/rtsp-h264-mjpeg-interface';
import { has } from 'lodash';
import { getAssetsCacheMap } from '../../utils/assets-helper';
@Component({
  tag: 'lar-intercom',
  styleUrl: 'intercom.scss',
  shadow: true,
  assetsDirs: ['assets']

})
export class LarvaIntercom {
  @Element() el!: HTMLElement;

  /////// LarvaNode base properties
  /**
   * Component main icon
   */
  @Prop({ mutable: true }) icon?: string = 'intercom';
  /**
   * The color to use from your application's color palette.
   * Detrouble options are: `"primary"`, `"secondary"`, `"tertiary"`, `"success"`, `"warning"`, `"danger"`, `"light"`, `"medium"`, and `"dark"`.
   */
  @Prop({ mutable: true }) color?: Color = 'primary';
  /**
   * The color to use from your application's color palette for Components modal window.
   */
  @Prop({ mutable: true }) colorModal?: Color;
  /**
   * The color to use from your application's color palette for inputs
   */
  @Prop({ mutable: true }) colorInputs?: Color;
  /**
   * The color to use from your application's color palette for indication icon
   */
  @Prop() colorIconSmall?: Color;
  /**
   * Disable quck actions
   */
  @Prop() disableQuickActions: boolean = false;
  /**
   * Hide node titles
   */
  @Prop() hideTitles?: boolean;
  /**
   * Component superscript title
   */
  @Prop() supTitle?: string;
  /**
   * Component subtitle
   */
  @Prop() subTitle?: string;
  /**
   * Is logging for this component enabled (lar-log subcomponent loaded)
   */
  @Prop() log: boolean = false;
  /**
   * Try to play ring sound if ringing
   * Please note that you may get DOMException: play() failed because the user didn't interact with the document first
   * in webkit audio must be handled externally or user interaction policy needs to be disabled
   */
  @Prop() playSound: boolean = false;
  /**
   * Component main title
   */
  @Prop() mainTitle!: string;
  /**
   * Node size
   */
  @Prop() nodeSize?: Size = 'default';
  /**
   * @see {@link ../readme.md} chapter "Components input and output" for further information.
   */
  @Event() output!: EventEmitter;
  /**
   * @see {@link ../readme.md} chapter "Sub-Components requests and responses" for further information.
   */
  @Event() request!: EventEmitter;
  /////// LarvaNode base properties and events - end

  /**
   * Use upstream proxy for cameras
   */
  @Prop() useUplinkProxy: boolean = true;
   /**
    * server for cameras
    */
  @Prop({ mutable: true }) server!: string;

  @State() loading: boolean = true;
  @State() camera?: CameraOpts;
  @State() sip?: SipOpts;
  @State() buttons: Button[] = [];
  @State() ring: boolean = false;

  public async componentDidRender() { // if modal not opened, open after each update
    if (this.ring === true) {
      const modal = await this.node.getModalComponent();
      if (modal) {
        return;
      }
      if (this.playSound) {
        try {
          const cachedFile = getAssetsCacheMap().get(`doorbell.mp3`);
          const file = cachedFile ? cachedFile : getAssetPath(`./assets/doorbell.mp3`);
          const player = new Audio(file);
          await player.play();
        } catch (err) {
          console.error(err);
        }
      }
      await this.node.openModal();
    }
  }

  private node!: HTMLLarNodeElement;

  public componentWillLoad() {
    this.output.emit();
  }

  public componentDidLoad() {
    const el = this.el.shadowRoot || this.el;
    this.node = el.querySelector('lar-node') as HTMLLarNodeElement;
  }

  /**
   * Larva error input
   */
  @Method()
  public async error(data: any) {
    if (this.node) {
      this.node.error(data);
    }
    this.loading = false;
  }
  /**
   * Larva input message
   */
  @Method()
  public async input(data: DataIn) {
    this.loading = false;
    // config data
    if (has(data, 'camera')) {
      this.camera = data.camera;
    }
    // we have upstream ready
    // notify component that it can start streaming
    if (has(data, 'upstream')) {
      const modalComponent = await this.node.getModalComponent();
      if (modalComponent?.element) {
        await (modalComponent.element as unknown as HTMLLarIntercomContentElement).open();
      }
    }
    if (has(data, 'sip')) {
      this.sip = data.sip;
    }
    if (has(data, 'buttons') && Array.isArray(data.buttons)) {
      this.buttons = data.buttons;
    }
    // somebody has pressed ring button?
    this.ring = has(data, 'ring') && data.ring === true;
  }

  private handleOutoput(data: any) {
    this.output.emit(data);
  }

  public render() {
    const componentProps = {
      onOutput: (data: CustomEvent) => this.handleOutoput(data.detail),
      onLoading: (data: CustomEvent) => this.loading = data.detail,
      disabled: this.loading,
      camera: this.camera,
      useUplinkProxy: this.useUplinkProxy,
      buttons: this.buttons,
      sip: this.sip,
      server: this.server,
      ring: this.ring,
      colorInputs: this.colorInputs || this.color,
    };
    return [
      <lar-node
        icon={this.icon}
        hideTitles={this.hideTitles}
        color={this.color}
        supTitle={this.supTitle}
        mainTitle={this.mainTitle}
        subTitle={this.subTitle}
        colorModal={this.colorModal}
        colorInputs={this.colorInputs}
        colorIconSmall={this.colorIconSmall}
        nodeSize={this.nodeSize}
        loading={this.loading}
        headerSizeModal={this.camera? 'small' : 'default'}
        log={this.log}
        component="lar-intercom-content"
        componentProps={componentProps}
        >
        <slot />
      </lar-node>
    ];
  }
}
